import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import {
  BarChartConfigurations, Statistics, Value, Bar, RangeDefinition, GridLine,
  BarDefinition, Path, Line, Title, LegendItem
} from "./bar-chart.models";
import { CommonModule } from '@angular/common';

@Component({
  selector: 'kla-bar-chart',
  templateUrl: './bar-chart.component.html',
  styleUrls: ['./bar-chart.component.scss'],
  standalone: true,
  imports: [ CommonModule ]
})
export class BarChartComponent implements OnInit, OnChanges {

  @Input()
  public width: number = 800;

  @Input()
  public height: number = 500;

  @Input()
  public padding: number = 40;

  @Input()
  public fontSize: number = 12;

  @Input()
  public fontFamily: string = "Arial";

  @Input()
  public fontColor: string = "#767777";

  @Input()
  public config: BarChartConfigurations;

  public show:boolean = false;
  public gridLines:Array<GridLine> = [];
  public paths:Array<Path> = [];
  public lines:Array<Line> = [];
  public names:Array<Title> = [];
  public leftTitle:Title;
  public rightTitle:Title;
  public legend:Array<LegendItem> = [];
  public maxRangeValueWidth: number;


  constructor() {
  }

  shortNumber(number: any) {
    if (number == null || number == undefined) return "";
    if (isNaN(number)) {
      return number;
    } else {
      let value = parseInt(number);
      if (isNaN(value)) {
        return "";
      }
      if (value >= 10) {
        return value;
      } else {
        return parseFloat(number).toFixed(1);
      }
    }
  }

  ngOnInit() {
    this.draw();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.gridLines = [];
    this.paths = [];
    this.lines = [];
    this.names = [];
    this.legend = [];
    this.draw();
  }

  private draw() {
    if (this.width && this.height) {
      if (this.config && this.config.values && this.config.values.length > 0) {
        if (this.config.values[0].bars.length > 0) {
          if (this.config.barDefinitions.length > 0) {
            if (this.config.rangeDefinitions.length > 0) {
              let statistics: Statistics = this.calculateStatistics();
              this.calculateGridValues(statistics);
              this.calculatePathPositions(statistics);
              this.calculateTextPositions(statistics);
              this.calculateLegendPositions(statistics);
              this.calculateTitleTextPositions();
              this.show = true;
            }
          }
        }
      }
    }
  }

  private calculateStatistics() {
    let statistics: Statistics = <Statistics>{
      numberOfBars: 0,
      maxValueLeft: -99999999,
      minValueLeft: 99999999,
      maxValueRight: -99999999,
      minValueRight: 99999999,
      maxRangeValueWidth: -99999999,
      maxRangeNameWidth: -99999999,
      maxBarNameWidth: -99999999
    };
    this.calculateStatisticValues(statistics);
    this.calculateStatisticTextSizes(statistics);
    return statistics;
  }

  private calculateStatisticValues(statistics: Statistics) {
    if (this.config.scaleValues) {
      statistics.minValueRight = this.config.scaleValues.scaleRightMin;
      statistics.maxValueRight = this.config.scaleValues.scaleRightMax;
      statistics.minValueLeft = this.config.scaleValues.scaleLeftMin;
      statistics.maxValueLeft = this.config.scaleValues.scaleLeftMax;
    }
    else {
      this.config.values.forEach((valueItem: Value) => {
        valueItem.bars.forEach((item: Bar) => {
          if (item.scaleOrientation == 'Right') {
            if (item.value > statistics.maxValueRight) {
              statistics.maxValueRight = item.value;
            }
            if (item.value < statistics.minValueRight) {
              statistics.minValueRight = item.value;
            }
          }
          else {
            if (item.value > statistics.maxValueLeft) {
              statistics.maxValueLeft = item.value;
            }
            if (item.value < statistics.minValueLeft) {
              statistics.minValueLeft = item.value;
            }
          }
        });
      });
    }
  }

  private calculateStatisticTextSizes(statistics: Statistics) {
    this.config.rangeDefinitions.forEach((range: RangeDefinition) => {
      range.shortName = range.name.length > 15 ? (range.name.substring(0, 12) + "...") : range.name;
      range.shortValue = range.value.length > 15 ? (range.value.substring(0, 12) + "...") : range.value;
      let nameWidth = this.calculateTextWidth(range.shortName, this.fontSize, this.fontFamily);
      let valueWidth = this.calculateTextWidth(range.shortValue, this.fontSize, this.fontFamily);
      if (nameWidth > statistics.maxRangeNameWidth) {
        statistics.maxRangeNameWidth = nameWidth;
      }
      if (valueWidth > statistics.maxRangeValueWidth) {
        statistics.maxRangeValueWidth = valueWidth;
      }
    });
    this.maxRangeValueWidth = statistics.maxRangeValueWidth;
    this.config.barDefinitions.forEach((bar:BarDefinition) => {
      bar.shortName = bar.name.length > 20 ? (bar.name.substring(0, 12) + "...") : bar.name;
      let nameWidth = this.calculateTextWidth(bar.shortName, this.fontSize, this.fontFamily);
      if (nameWidth > statistics.maxBarDefinitionNameWidth) {
        statistics.maxBarDefinitionNameWidth = nameWidth;
      }
      if (bar.type == 'BAR') {
        statistics.numberOfBars++
      }
    });
    this.config.values.forEach((value: Value) => {
      value.shortName = value.name.length > 20 ? (value.name.substring(0, 12) + "...") : value.name;
      let nameWidth = this.calculateTextWidth(value.shortName, this.fontSize, this.fontFamily);
      if (nameWidth + 15 > statistics.maxBarNameWidth) {
        statistics.maxBarNameWidth = nameWidth + 15;
      }
    });
  }


  private calculateGridValues(statistics: Statistics) {
    let barLength = this.config.barDefinitions.length;
    let rangeLength = this.config.rangeDefinitions.length;
    let leftPadding = this.padding + statistics.maxRangeValueWidth + 15;
    let rightPadding = this.padding + statistics.maxRangeNameWidth + 15;
    let bottomPadding = this.padding + statistics.maxBarNameWidth;
    let topPadding = this.padding;
    let width = this.width - leftPadding - rightPadding;
    let height = this.height - bottomPadding - topPadding;
    this.gridLines = [];
    let gridLineHeight = height / (rangeLength - 1);
    statistics.leftBottomChartCorner = { x: leftPadding, y: height + topPadding };
    statistics.topRightChartCorner = { x: leftPadding + width, y: topPadding };
    this.config.rangeDefinitions.forEach((range: RangeDefinition, index: number) => {
      this.gridLines.push({
        from: {
          x: leftPadding,
          y: height + topPadding - (gridLineHeight * index)
        },
        to: {
          x: leftPadding + width,
          y: height + topPadding - (gridLineHeight * index)
        },
        range: range
      })
    });
  }

  private calculatePathPositions(statistics: Statistics) {
    let width = statistics.topRightChartCorner.x - statistics.leftBottomChartCorner.x;
    let height = statistics.leftBottomChartCorner.y - statistics.topRightChartCorner.y;

    this.config.values.forEach((value: Value, valueIndex: number) => {
      let barIndex: number = 0;
      let circleIndex: number = 1;
      value.bars.forEach((bar: Bar) => {
        let difference = (bar.scaleOrientation == 'Right') ? statistics.maxValueRight - statistics.minValueRight : statistics.maxValueLeft - statistics.minValueLeft
        let heightUnit = height / difference;
        let widthUnit = width / (this.config.values.length);

        let barDefinition = this.config.barDefinitions[bar.barDefinitionIndex];
        let point = null;
        let definition = null;
        if (bar.scaleOrientation == 'Right')
          bar.value = (bar.value < statistics.minValueRight) ? statistics.minValueRight : (bar.value > statistics.maxValueRight) ? statistics.maxValueRight : bar.value;
        else
          bar.value = (bar.value < statistics.minValueLeft) ? statistics.minValueLeft : (bar.value > statistics.maxValueLeft) ? statistics.maxValueLeft : bar.value;

        let y = (bar.scaleOrientation == 'Right') ? this.calculateCircleY(bar.value, statistics.maxValueRight, statistics.minValueRight, heightUnit)
          : this.calculateCircleY(bar.value, statistics.maxValueLeft, statistics.minValueLeft, heightUnit);
        let totalBarWidth = widthUnit / (statistics.numberOfBars + 1);
        let barPadding = totalBarWidth / 8;
        let barWidth = totalBarWidth - 2 * barPadding;
        if (barDefinition.type == 'CIRCLE') {
          point = {
            x: statistics.leftBottomChartCorner.x + valueIndex * widthUnit + barWidth + circleIndex * totalBarWidth - barPadding,
            y: statistics.leftBottomChartCorner.y - y
          };
          circleIndex++;
        } else {
          let x1 = statistics.leftBottomChartCorner.x + valueIndex * widthUnit + barWidth + barIndex * totalBarWidth;
          let y1 = statistics.leftBottomChartCorner.y - y;
          definition = (bar.value != statistics.minValueLeft && bar.value != statistics.minValueRight)? this.createBarPath(x1, y1, x1 + barWidth, statistics.leftBottomChartCorner.y)
                                                                                                      : this.createBottomLineBar(x1, y1, x1 + barWidth, statistics.leftBottomChartCorner.y)
          barIndex++;
        }
        this.paths.push({
          bar: bar,
          barDefinition: barDefinition,
          point: point,
          definition: definition
        })
      });
    });
  }

  private calculateTextPositions(statistics: Statistics) {
    let width = statistics.topRightChartCorner.x - statistics.leftBottomChartCorner.x;
    let widthUnit = width / (this.config.values.length);
    let totalBarWidth = widthUnit / (statistics.numberOfBars + 1);
    let barPadding = totalBarWidth / 8;
    let barWidth = totalBarWidth - 2 * barPadding;
    this.config.values.forEach((value: Value, valueIndex: number) => {
      this.lines.push({
        from: {
          x: statistics.leftBottomChartCorner.x + valueIndex * widthUnit + barWidth,
          y: statistics.leftBottomChartCorner.y
        },
        to: {
          x: statistics.leftBottomChartCorner.x + valueIndex * widthUnit + barWidth,
          y: this.height - this.padding
        },
      });
      this.names.push({
        name: value.name,
        shortName: value.shortName,
        point: {
          x: statistics.leftBottomChartCorner.x + valueIndex * widthUnit + barWidth + 1.5 * this.fontSize,
          y: this.height - this.padding
        }
      });
    });
  }

  private calculateTitleTextPositions() {
    let leftHeight = this.calculateTextWidth(this.config.leftTitle, this.fontSize + 2, this.fontFamily);
    this.leftTitle = {
      name: this.config.leftTitle,
      shortName: this.config.leftTitle,
      point: {
        x: this.padding - 10,
        y: (this.height - leftHeight) / 2 + leftHeight
      }
    };
    let rightHeight = this.calculateTextWidth(this.config.leftTitle, this.fontSize + 2, this.fontFamily);
    this.rightTitle = {
      name: this.config.rightTitle,
      shortName: this.config.rightTitle,
      point: {
        x: this.width - this.padding + 10,
        y: (this.height - rightHeight) / 2
      }
    };
  }

  private calculateLegendPositions(statistics: Statistics) {
    let width = statistics.topRightChartCorner.x - statistics.leftBottomChartCorner.x;
    let widthUnit = width / (this.config.values.length);
    let totalBarWidth = widthUnit / (statistics.numberOfBars + 1);
    let barPadding = totalBarWidth / 8;
    let barWidth = totalBarWidth - 2 * barPadding;
    this.config.barDefinitions.forEach((barDefinition: BarDefinition, index: number) => {
      this.legend.push({
        barDefinition: barDefinition,
        point: {
          x: statistics.leftBottomChartCorner.x + barWidth + index * width / this.config.barDefinitions.length,
          y: this.height - this.padding + this.fontSize + 20
        }
      })
    });
  }


  private calculateCircleY(value: number, maxValue: number, minValue: number, heightUnit: number) {
    if (maxValue >= 0 && minValue >= 0) {
      return (value - minValue) * heightUnit;
    }
    if (maxValue >= 0 && minValue <= 0) {
      return (value - minValue) * heightUnit;
    }
    if (maxValue <= 0 && minValue <= 0) {
      return (value - maxValue) * heightUnit;
    }
  }

  private createBarPath(x1: number, y1: number, x2: number, y2: number) {
    let coefficient = (x2 - x1) * 6 / 10;
    y1 = y1 + coefficient * 22 / 30;
    y1 = y1 > y2 ? y2 : y1;
    return "M" + x1 + " " + y1 +
      " L " + x1 + " " + y2 +
      " L " + x2 + " " + y2 +
      " L " + x2 + " " + y1 +
      " M" + x1 + " " + y1 +
      " C " + x1 + " " + (y1 - coefficient) + ", " + x2 + " " + (y1 - coefficient) + ", " + x2 + " " + y1;
  }

  private createBottomLineBar(x1: number, y1: number, x2: number, y2: number){
    return "M" + x1 + " " + y1 +
      " L " + x1 + " " + y2 +
      " L " + x2 + " " + y2 +
      " L " + x2 + " " + y1 +
      " M" + x1 + " " + y1;
  }

  private calculateTextWidth(text: string, fontSize: number, fontFamily: string): number {
    let canvas = document.createElement("canvas");
    let context = canvas.getContext("2d");
    context.font = fontSize + "px " + fontFamily;
    return context.measureText(text).width;
  }
}
